import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { ShieldCheck, LogIn } from "lucide-react";

const generateCodeVerifier = () => {
  const array = new Uint8Array(32);
  window.crypto.getRandomValues(array);
  return Array.from(array, (b) => ("0" + b.toString(16)).slice(-2)).join("");
};

const generateCodeChallenge = async (verifier) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await window.crypto.subtle.digest("SHA-256", data);
  const base64 = btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
  return base64;
};

const GOOGLE_AUTH_URL = "https://accounts.google.com/o/oauth2/v2/auth";

export default function AdminLogin({ backendUrl }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const redirectUri = `${window.location.origin}/auth/callback`;

  const startLogin = async () => {
    try {
      setError("");
      setLoading(true);
      const verifier = generateCodeVerifier();
      const challenge = await generateCodeChallenge(verifier);

      window.sessionStorage.setItem("hti_oauth_verifier", verifier);

      const params = new URLSearchParams({
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: "code",
        scope: "openid email profile",
        code_challenge: challenge,
        code_challenge_method: "S256",
        access_type: "offline",
        prompt: "consent",
      });

      window.location.href = `${GOOGLE_AUTH_URL}?${params.toString()}`;
    } catch (e) {
      console.error(e);
      setError("Tidak dapat memulai proses login Google.");
      setLoading(false);
    }
  };

  const handleDevBypass = async (e) => {
    e.preventDefault();
    try {
      setError("");
      setLoading(true);
      // Opsi bypass sangat terbatas: hanya untuk lokal jika backend tidak tersedia oauth
      const token = window.prompt("Masukkan token admin sementara (untuk pengujian lokal)");
      if (token) {
        window.localStorage.setItem("hti_admin_token", token);
        navigate("/admin/leads");
      }
    } catch (err) {
      console.error(err);
      setError("Bypass gagal.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10 text-slate-900 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-md flex-col gap-6">
        <Card className="border-slate-200 bg-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ShieldCheck className="h-4 w-4 text-emerald-500" />
              <span>Admin Login</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <p className="text-xs text-slate-600">
              Masuk dengan akun Google yang terdaftar sebagai admin untuk melihat daftar lead konsultasi.
            </p>
            <Button
              onClick={startLogin}
              disabled={loading}
              className="w-full rounded-full bg-slate-900 text-xs font-semibold text-white hover:bg-slate-800"
            >
              <LogIn className="mr-2 h-3.5 w-3.5" />
              {loading ? "Mengalihkan ke Google..." : "Sign in with Google"}
            </Button>
            {error && (
              <p className="text-xs text-red-600">
                {error}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
